import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Clock, CalendarDays } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import InlineAccountForm from "@/components/checkout/InlineAccountForm";
import PackageSchedulerModal from "@/components/PackageSchedulerModal";
import ShareReflectionPrompt from "@/components/gauge/ShareReflectionPrompt";

const MAX_ATTEMPTS = 6;
const INTERVAL = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const provider = params.get("provider");
  const paypalToken = params.get("token"); // PayPal appends ?token=<order id>
  const isMembership = params.get("type") === "membership";
  const isPackage = params.get("type") === "package";
  const isGiftCert = params.get("type") === "giftcert";
  const statusPath = isMembership
    ? `/memberships/subscription/status/${sessionId}`
    : isPackage
      ? `/packages/purchase/status/${sessionId}`
      : isGiftCert
        ? `/gift-certificates/status/${sessionId}`
        : `/checkout/status/${sessionId}`;
  const [state, setState] = useState("checking"); // checking | paid | expired | error
  const [isGift, setIsGift] = useState(false);
  const [giftCode, setGiftCode] = useState("");
  const [giftDeliverOn, setGiftDeliverOn] = useState("");
  const [pkgCredit, setPkgCredit] = useState(null);
  const [schedOpen, setSchedOpen] = useState(false);
  const attempts = useRef(0);
  const { user } = useAuth();
  const [guestEmail, setGuestEmail] = useState("");
  const [bookingToken, setBookingToken] = useState("");
  const [awaiting, setAwaiting] = useState(false);

  useEffect(() => {
    // PayPal / Venmo: capture the approved order once on return.
    if (provider === "paypal" && paypalToken) {
      (async () => {
        try {
          await api.post(`/paypal/order/${paypalToken}/capture`);
          setState("paid");
        } catch { setState("error"); }
      })();
      return;
    }
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(statusPath);
        if (data.payment_status === "paid") { setState("paid"); setGuestEmail(data.email || data.buyer_email || ""); setBookingToken(data.booking_token || ""); setAwaiting(data.agreements_status === "awaiting"); setIsGift(!!data.is_gift); if (data.code) setGiftCode(data.code); if (data.deliver_on) setGiftDeliverOn(data.deliver_on); return; }
        if (data.status === "expired") { setState("expired"); return; }
        if (attempts.current >= MAX_ATTEMPTS) { setState("error"); return; }
        attempts.current += 1;
        timer = setTimeout(poll, INTERVAL);
      } catch {
        if (attempts.current >= MAX_ATTEMPTS) { setState("error"); return; }
        attempts.current += 1;
        timer = setTimeout(poll, INTERVAL);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, provider, paypalToken]);

  // After a (non-gift) package purchase, offer to schedule the sessions right away.
  useEffect(() => {
    if (state !== "paid" || !isPackage || isGift) return;
    api.get("/me/package-credits")
      .then((r) => setPkgCredit((r.data.credits || []).find((c) => c.source_sale_id === sessionId) || null))
      .catch(() => {});
  }, [state, isPackage, isGift, sessionId]);

  const content = {
    checking: { icon: <Loader2 size={48} className="text-[#144F75] mx-auto mb-4 animate-spin" />, title: "Confirming your payment…", text: "Please wait a moment while we verify your payment." },
    paid: { icon: <CheckCircle2 size={48} className="text-[#144F75] mx-auto mb-4" />, title: "Payment successful!", text: "Your order is confirmed. Check your dashboard for details." },
    expired: { icon: <Clock size={48} className="text-[#D98E73] mx-auto mb-4" />, title: "Session expired", text: "Your payment session expired. Please try booking again." },
    error: { icon: <XCircle size={48} className="text-[#D98E73] mx-auto mb-4" />, title: "We couldn't confirm payment", text: "If you completed payment, check your dashboard shortly. Otherwise please try again." },
  }[state];

  return (
    <div className="min-h-[70vh] grid place-items-center px-6" data-testid="payment-success-page">
      <div className="eyw-card p-10 max-w-md w-full text-center">
        {content.icon}
        <h1 className="font-serif text-3xl text-[#1A242B]" data-testid="payment-status">{content.title}</h1>
        <p className="text-[#4A5D6B] mt-3">{content.text}</p>
        {state === "paid" && isGiftCert && giftCode && (
          <div className="mt-6 rounded-2xl border border-[#D98E73]/40 bg-[#D98E73]/[0.06] p-5" data-testid="giftcard-success">
            <p className="text-sm text-[#4A5D6B]">Your gift card is ready. {giftDeliverOn ? `We'll email the code to the recipient on ${giftDeliverOn}.` : "We've emailed the code to the recipient."}</p>
            <p className="font-mono text-xl tracking-widest text-[#144F75] mt-2" data-testid="giftcard-success-code">{giftCode}</p>
          </div>
        )}
        {state === "paid" && pkgCredit && (
          <div className="mt-6 rounded-2xl border border-[#144F75]/20 bg-[#144F75]/[0.05] p-5 text-left" data-testid="schedule-prompt">
            <p className="text-sm text-[#1A242B]"><b>{pkgCredit.package_name}</b> — {pkgCredit.sessions_remaining} sessions ready to schedule.</p>
            <p className="text-xs text-[#4A5D6B] mt-1">Book one or all of them now, or anytime later from your dashboard.</p>
            <button onClick={() => setSchedOpen(true)} data-testid="schedule-now-btn" className="eyw-btn w-full mt-4 justify-center">
              <CalendarDays size={17} /> Schedule my sessions
            </button>
          </div>
        )}
        {state === "paid" && awaiting && (
          <div className="mt-6 rounded-2xl border border-[#D98E73]/40 bg-[#D98E73]/[0.06] p-5 text-left" data-testid="payment-agreements-pending">
            <p className="text-sm text-[#1A242B]"><b>One more step:</b> your booking is held until you sign the required agreements.</p>
            <Link to={bookingToken ? `/booking-agreements/${bookingToken}` : "/dashboard"} data-testid="payment-sign-agreements" className="eyw-btn w-full justify-center mt-3">Review & sign agreements</Link>
          </div>
        )}
        {state === "paid" && user === false && (
          <div className="mt-6" data-testid="payment-guest-account">
            <InlineAccountForm defaultEmail={guestEmail} bookingToken={bookingToken} />
          </div>
        )}
        {state === "paid" && !isGift && !isGiftCert && user && <ShareReflectionPrompt />}
        <div className="flex gap-3 justify-center mt-7">
          <Link to={user ? "/dashboard" : "/"} className="eyw-btn">{user ? "Go to Dashboard" : "Back to home"}</Link>
          <Link to="/events" className="eyw-btn-ghost">Browse More</Link>
        </div>
      </div>
      {schedOpen && pkgCredit && (
        <PackageSchedulerModal credit={pkgCredit} onClose={() => setSchedOpen(false)} />
      )}
    </div>
  );
}
